import { useState, useEffect } from 'react';
import { useProtocolStore } from '@/modules/protocol/protocol-store';
import { db } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import { validateForFinalization, type ValidationResult } from '@/lib/finalization-validator';
import { RI_PROTOCOLS, type Protocol, type ProtocolRequirement } from '@moquintrend/protocol-engine';
import type { EPCR, ComplianceFieldEntry } from '@/types';
import { cn } from '@/lib/cn';
import {
  ClipboardList, Check, AlertTriangle, CheckCircle2, XCircle,
  Paperclip, Info,
} from 'lucide-react';
import { SignatureLedger } from '../SignatureLedger';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

/**
 * "Required Documentation" tab. Dynamically shows condition-specific
 * required fields based on active protocols. Displays the live
 * finalization validator so the provider can see exactly what's
 * blocking QA sign-off.
 */
export function RequiredDocTab({ epcr, onUpdate }: Props) {
  const { user } = useAuthStore();
  const { activeProtocols } = useProtocolStore();
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    validateForFinalization(epcr).then(setValidation);
  }, [epcr.id, refreshKey, JSON.stringify(epcr.compliance_fields)]);

  const protocols = activeProtocols();

  async function setField(key: string, value: string | number | boolean | null) {
    if (!user) return;
    const entry: ComplianceFieldEntry = {
      value,
      entered_at: new Date().toISOString(),
      entered_by: user.id,
    };
    const next = { ...(epcr.compliance_fields ?? {}), [key]: entry };
    onUpdate({ compliance_fields: next });
    setRefreshKey((k) => k + 1);
  }

  const getValue = (key: string): string | number | boolean | null => {
    return epcr.compliance_fields?.[key]?.value ?? null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardList size={20} className="text-brand-400" />
        <h3 className="text-lg font-semibold text-slate-200">Required Documentation</h3>
      </div>

      {/* Live validation summary */}
      {validation && (
        <ValidationSummary validation={validation} />
      )}

      {/* Required fields by protocol */}
      {protocols.length === 0 ? (
        <div className="card text-center py-6 text-slate-500 text-sm">
          No protocols active. Select a dispatch reason or impression to populate required fields.
        </div>
      ) : (
        protocols.map((protocol) => (
          <ProtocolRequirementsSection
            key={protocol.id}
            protocol={protocol}
            epcr={epcr}
            getValue={getValue}
            setField={setField}
          />
        ))
      )}

      {/* Validator full detail */}
      {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
        <FullIssueList validation={validation} />
      )}

      {/* Signature ledger — three slots for EMT attestation, patient
          consent / refusal, and receiving clinician handoff. Sprint J. */}
      <SignatureLedger epcr={epcr} />
    </div>
  );
}

function ValidationSummary({ validation }: { validation: ValidationResult }) {
  return (
    <div className={cn(
      'rounded-lg border p-4',
      validation.canFinalize
        ? 'border-green-700 bg-green-950/30'
        : 'border-amber-700 bg-amber-950/30',
    )}>
      <div className="flex items-start gap-3">
        {validation.canFinalize ? (
          <CheckCircle2 size={24} className="shrink-0 text-ems-green" />
        ) : (
          <AlertTriangle size={24} className="shrink-0 text-ems-amber" />
        )}
        <div className="flex-1">
          {validation.canFinalize ? (
            <>
              <div className="font-semibold text-ems-green">Report meets finalization requirements</div>
              <div className="mt-1 text-sm text-green-300">
                {validation.warnings.length > 0 && `${validation.warnings.length} warning${validation.warnings.length !== 1 ? 's' : ''} (not blocking). `}
                Provider may approve narrative and QA can sign off.
              </div>
            </>
          ) : (
            <>
              <div className="font-semibold text-ems-amber">
                {validation.errors.length} item{validation.errors.length !== 1 ? 's' : ''} blocking finalization
              </div>
              <div className="mt-1 text-sm text-amber-300">
                Resolve each error below before QA sign-off.
                {validation.warnings.length > 0 && ` ${validation.warnings.length} additional warning${validation.warnings.length !== 1 ? 's' : ''}.`}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProtocolRequirementsSection({
  protocol, epcr, getValue, setField,
}: {
  protocol: Protocol;
  epcr: EPCR;
  getValue: (key: string) => string | number | boolean | null;
  setField: (key: string, value: string | number | boolean | null) => void;
}) {
  if (!protocol.requirements?.length) return null;

  // Filter out requirements whose conditionalOn decision hasn't been made
  const applicable = protocol.requirements.filter((req) => {
    if (!req.conditionalOn) return true;
    const decision = epcr.protocol_state?.decisions.find(
      d => d.protocol_id === protocol.id && d.step_order === req.conditionalOn!.stepOrder,
    );
    return decision?.choice === req.conditionalOn.choice;
  });

  if (applicable.length === 0) return null;

  const completed = applicable.filter(r => {
    const v = getValue(r.key);
    return v !== null && v !== '' && v !== undefined;
  }).length;

  return (
    <section className="rounded-lg border border-slate-700 overflow-hidden">
      <header className="bg-slate-800 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-200">{protocol.name}</div>
          <div className="text-xs text-slate-400">{protocol.source} v{protocol.version}</div>
        </div>
        <div className="text-sm">
          <span className="text-slate-200 font-semibold">{completed}</span>
          <span className="text-slate-500"> / {applicable.length} fields</span>
        </div>
      </header>
      <div className="divide-y divide-slate-800 bg-slate-900">
        {applicable.map((req) => (
          <RequirementField
            key={req.key}
            req={req}
            value={getValue(req.key)}
            onChange={(v) => setField(req.key, v)}
            locked={epcr.locked}
          />
        ))}
      </div>
    </section>
  );
}

function RequirementField({
  req, value, onChange, locked,
}: {
  req: ProtocolRequirement;
  value: string | number | boolean | null;
  onChange: (value: string | number | boolean | null) => void;
  locked: boolean;
}) {
  const hasValue = value !== null && value !== '' && value !== undefined;

  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          hasValue
            ? 'border-ems-green bg-ems-green text-white'
            : req.required
              ? 'border-ems-amber bg-transparent'
              : 'border-slate-600',
        )}>
          {hasValue && <Check size={12} />}
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm font-medium text-slate-200">{req.label}</label>
            {req.required && (
              <span className="rounded bg-amber-900 px-1.5 py-0.5 text-[10px] text-amber-300">REQUIRED</span>
            )}
          </div>
          {req.description && (
            <p className="text-xs text-slate-400">{req.description}</p>
          )}
          <p className="text-[10px] text-slate-500 italic">{req.cite}</p>

          {/* Input varies by type */}
          <div className="mt-2">
            <FieldInput req={req} value={value} onChange={onChange} disabled={locked} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldInput({
  req, value, onChange, disabled,
}: {
  req: ProtocolRequirement;
  value: string | number | boolean | null;
  onChange: (value: string | number | boolean | null) => void;
  disabled: boolean;
}) {
  switch (req.type) {
    case 'boolean':
      return (
        <div className="flex gap-2">
          {[true, false].map((v) => (
            <button
              key={String(v)}
              type="button"
              disabled={disabled}
              onClick={() => onChange(v)}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-medium min-h-[2.5rem] transition-colors',
                value === v
                  ? v ? 'bg-ems-green text-white' : 'bg-slate-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700',
              )}
            >
              {v ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      );
    case 'select':
      return (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="input-field"
          disabled={disabled}
        >
          <option value="">Select...</option>
          {(req.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
    case 'number':
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            pattern="[0-9.]*"
            min={req.min}
            max={req.max}
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            className="input-field"
            disabled={disabled}
          />
          {req.unit && <span className="text-sm text-slate-400">{req.unit}</span>}
        </div>
      );
    case 'datetime':
      return (
        <div className="flex gap-1">
          <input
            type="datetime-local"
            value={typeof value === 'string' ? value.slice(0, 16) : ''}
            onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="input-field flex-1"
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => onChange(new Date().toISOString())}
            disabled={disabled}
            className="px-2 border border-slate-600 bg-slate-800/80 text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700 disabled:opacity-40"
            title="Set to current time"
          >
            Now
          </button>
        </div>
      );
    case 'text':
      return (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="input-field"
          disabled={disabled}
        />
      );
    case 'attachment':
      return (
        <div className="rounded-lg border border-dashed border-slate-600 bg-slate-800 p-3 text-xs text-slate-400 flex items-center gap-2">
          <Paperclip size={14} />
          Upload via the <a href="#attachments" className="underline hover:text-brand-400">Attachments</a> tab with category <code className="rounded bg-slate-900 px-1">{req.attachmentCategory}</code>.
        </div>
      );
    default:
      return <div className="text-xs text-slate-500">Unsupported field type: {req.type}</div>;
  }
}

function FullIssueList({ validation }: { validation: ValidationResult }) {
  const allIssues = [...validation.errors, ...validation.warnings];
  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900 overflow-hidden">
      <header className="bg-slate-800 px-4 py-3 flex items-center gap-2">
        <Info size={16} className="text-slate-400" />
        <span className="font-semibold text-slate-200">Full Validation Detail</span>
      </header>
      <ul className="divide-y divide-slate-800">
        {allIssues.map((issue) => (
          <li key={issue.id} className="p-3 flex gap-3">
            {issue.severity === 'error' ? (
              <XCircle size={16} className="shrink-0 mt-0.5 text-ems-red" />
            ) : (
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-ems-amber" />
            )}
            <div className="flex-1">
              <div className="text-sm text-slate-200">{issue.message}</div>
              {issue.cite && <div className="text-[10px] italic text-slate-500 mt-0.5">{issue.cite}</div>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
